import React, { Fragment, useState } from "react";
import { Box, Button, Grid2 as Grid, Stack, Typography } from "@mui/material";
import { FormAutoComplete, FormTextField, PasswordField } from "../../../components/generic/GenericTextFields";
import { ADMIN_ROLE, DEVELOPER_ROLE, ROLES } from "../../../enums/generalEnums";
import { useFormik } from "formik";
import { object, string } from "yup";
import { confrimPasswordValidation, emailValidation, passwordValidation } from "../../../helpers/genericFormValidations";
import { UploadImage } from "../../../components/ui/profileSetup/bio/UploadImage";
import { useTheme } from "@emotion/react";
import { Image, XCircle } from "@phosphor-icons/react";
import useNotiStack from "../../../hooks/useNotiStack";
import { createUser, updateUser } from "../../../config/apisauce";
import { GenericIconButton } from "../../../components/generic/GenericIconButton";
import { manageAddOrUpdate, removeNullValues } from "../../../helpers/arrayHelper";

export const CreateOrUpdateUserForm = (props) => {
  const {
    selectedUser,
    setOpenAddEditDialog,
    role,
    handleCloseAddEditDialog,
    setUsers
  } = props;

  const theme = useTheme();
  const { success, error } = useNotiStack();
  const [uploadedImages, setUploadedImages] = useState();
  const [profilePhoto, setProfilePhoto] = useState(selectedUser?.data?.details?.profile_picture);
  const [uploadedProfilePhotos, setUploadedProfilePhotos] = useState();
  const [previewUrls, setPreviewUrls] = useState([]);

  const handleCreateUser = async (values) => {
    const formData = new FormData();
    const createParams = {
      ...values,
      id: selectedUser?.data?.id,
      profile_picture_id: profilePhoto?.id
    };
    Object.entries(values).forEach(([key, value]) => {
      formData.append(key, value);
    });
    if (selectedUser?.isAdd) formData.append('profile_picture', uploadedProfilePhotos[0]);
    const response = selectedUser?.isAdd ? await createUser(formData) : await updateUser(removeNullValues(createParams));
    if (response.ok) {
      setUsers((prev) => {
        const currenList = { ...prev };
        currenList.data = manageAddOrUpdate(currenList.data, response?.data);
        return currenList;
      });
      handleCloseAddEditDialog();
      success(`Successfully ${selectedUser?.isAdd ? 'added' : 'updated'} user!`);
    }
  };

  const handleSelectImage = (event) => {
    const selectedImages = Array.from(event.target.files);
    const maxSize = 2 * 1024 * 1024;
    const validImages = [];
    const validPreviewUrls = [];

    selectedImages?.forEach(file => {
      if (file.size <= maxSize) {
        validImages.push(file);
        validPreviewUrls.push(URL.createObjectURL(file));
      } else {
        error(`This file: ${file.name} is too large. Upload less 2mb only.`)
      }
    });
    setUploadedProfilePhotos(validImages);
    setPreviewUrls(validPreviewUrls);
  };

  const formik = useFormik({
    initialValues: {
      name: selectedUser?.data?.name ?? "",
      email: selectedUser?.data?.email ?? "",
      role: selectedUser?.data?.roles[0]?.name ?? ADMIN_ROLE,
      password: "",
      password_confirmation: "",
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: object().shape({
      'name': string().trim().required('Name is required'),
      'role': string().required('Role is required'),
      ...emailValidation,
      ...confrimPasswordValidation,
      ...passwordValidation,
      ...(selectedUser?.isAdd ? { ...passwordValidation } : {
        'password': string()
          .min(8, 'Password must be at least 8 characters long')
          .matches(/[a-zA-Z]/, 'Password must contain at least one letter')
          .matches(/[0-9]/, 'Password must contain at least one number')
          .matches(/[^a-zA-Z0-9]/, 'Password must contain at least one special character')
      })
    }),
    onSubmit: (values) => handleCreateUser(values)
  });

  return (
    <Fragment>
      {
        selectedUser?.isAdd ? <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
          {
            uploadedProfilePhotos?.length > 0 ? previewUrls.map((url, index) => {
              return (
                <Box
                  key={`url-key-${index}`}
                  sx={{
                    backgroundImage: `url(${url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    width: '320px',
                    height: '340px',
                    borderRadius: '8px',
                    padding: '10px'
                  }}
                >
                  <Box display={'flex'} justifyContent={'flex-end'}>
                    <GenericIconButton
                      color={'primary.main'}
                      onClick={() => setUploadedProfilePhotos()}
                      tooltip={'Remove'}
                      icon={<XCircle size={24} weight="bold" />}
                    />
                  </Box>
                </Box>
              )
            }) : <Box
              component='label'
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '340px',
                border: '1px dashed #919EAB',
                borderRadius: '10px',
                cursor: 'pointer',
                width: '320px',
                backgroundColor: '#FFF'
              }}>
              <Stack sx={{ color: '#919EAB' }} alignItems={'center'}>
                <Image size={32} />
                <Typography fontWeight={700} textAlign={'center'}>
                  <span style={{ color: theme.palette.primary.main }}>Add </span> profile photo here
                  <span style={{ color: theme.palette.error.main }}> *</span>
                  <br></br>
                  <span style={{ color: theme.palette.error.main, fontSize: '12px', fontWeight: '400' }}>Profile photo is required</span>
                  <input
                    type="file"
                    name="resume"
                    onChange={handleSelectImage}
                    hidden
                    accept={".jpg,.jpeg,.png,.gif,.heic"}
                  />
                </Typography>
              </Stack>
            </Box>
          }
        </Box> : <UploadImage
          hideSetProfile={true}
          uploadedImages={uploadedImages}
          profilePhoto={profilePhoto}
          currentPhotoId={selectedUser?.data?.details?.profile_picture_id}
          setUploadedImages={setUploadedImages}
          userId={selectedUser?.data?.id}
          setProfilePhoto={setProfilePhoto}
        />
      }
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} mt={1}>
          <Grid item size={12}>
            <FormTextField
              value={formik?.values?.name}
              label={'Name'}
              placeholder={'Jane Doe'}
              error={Boolean(formik?.errors?.name)}
              helperText={formik?.errors?.name}
              handleChange={(value) => formik.setFieldValue('name', value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }}>
            <FormTextField
              value={formik?.values?.email}
              label={'Email'}
              placeholder={'example@email.com'}
              error={Boolean(formik?.errors?.email)}
              helperText={formik?.errors?.email}
              handleChange={(value) => formik.setFieldValue('email', value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }}>
            <FormAutoComplete
              disabled={role !== DEVELOPER_ROLE}
              value={formik?.values?.role}
              label={'Role'}
              options={ROLES}
              placeholder={'Please select'}
              error={Boolean(formik?.errors?.role)}
              helperText={formik?.errors?.role}
              handleChange={(value) => formik.setFieldValue('role', value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }}>
            <PasswordField
              value={formik?.values?.password}
              label={'Password'}
              placeholder={'********'}
              error={Boolean(formik?.errors?.password)}
              helperText={formik?.errors?.password}
              handleChange={(value) => formik.setFieldValue('password', value)}
              autoComplete="new-password"
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }}>
            <PasswordField
              value={formik?.values?.password_confirmation}
              label={'Confirm Password'}
              placeholder={'********'}
              error={Boolean(formik?.errors?.password_confirmation)}
              helperText={formik?.errors?.password_confirmation}
              handleChange={(value) => formik.setFieldValue('password_confirmation', value)}
              autoComplete="new-password"
            />
          </Grid>
        </Grid>
        <Box display={{ xs: 'default', sm: 'flex' }} alignItems={'flex-end'} justifyContent={'flex-end'} mt={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <Button variant="outlined" sx={{ width: { xs: '100%', sm: '250px' } }} onClick={() => setOpenAddEditDialog(false)}>Cancel</Button>
            <Button variant="contained" sx={{ width: { xs: '100%', sm: '250px' } }} type="submit">{selectedUser?.isAdd ? 'Add' : 'Save Changes'}</Button>
          </Stack>
        </Box>
      </form>
    </Fragment >
  );
};